import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { entities, settings, media, tags as tagsApi } from "../services/api";
import {
  BackIcon,
  CloseIcon,
  ProductIcon,
  ContainerIcon,
  LocationIcon,
  DocumentIcon,
  LinkIcon,
  SearchIcon,
  ChevronRightIcon,
  ListIcon,
  GridIcon,
} from "./Icons";
import TagMultiSelect from "./TagMultiSelect";
import SearchableSelect from "./SearchableSelect";
import { extractProductData, parseReceipt } from "../utils/receiptParser";

// Step indicators
const STEPS = {
  SELECT_TYPE: 1,
  PRODUCT_CHOICE: 2,
  LINK_EXISTING: 3,
  CREATE_NEW: 4,
};

function QRWizard({ qrCode, onComplete, onCancel }) {
  const navigate = useNavigate();
  const [step, setStep] = useState(STEPS.SELECT_TYPE);
  const [entityType, setEntityType] = useState(null);
  const [inputMode, setInputMode] = useState(null); // 'receipt' or 'manual'

  // Product list state for linking
  const [products, setProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [viewMode, setViewMode] = useState("list");
  const [linking, setLinking] = useState(false);
  const [linkingId, setLinkingId] = useState(null);

  // Form state for creating new
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [selectedParentId, setSelectedParentId] = useState("");
  const [parentEntity, setParentEntity] = useState(null);
  const [parentSearch, setParentSearch] = useState("");
  const [parentSearchResults, setParentSearchResults] = useState([]);
  const [showParentDropdown, setShowParentDropdown] = useState(false);

  // Product fields
  const [sku, setSku] = useState("");
  const [purchasePrice, setPurchasePrice] = useState("");
  const [retailPrice, setRetailPrice] = useState("");
  const [minSalePrice, setMinSalePrice] = useState("");
  const [listSalePrice, setListSalePrice] = useState("");
  const [quantity, setQuantity] = useState("1");
  const [condition, setCondition] = useState("new");
  const [productUrl, setProductUrl] = useState("");
  const [pendingImageUrl, setPendingImageUrl] = useState("");
  const [purchaseDate, setPurchaseDate] = useState("");

  // Container fields
  const [capacity, setCapacity] = useState("");
  const [containerType, setContainerType] = useState("");

  // Location fields
  const [address, setAddress] = useState("");
  const [locationType, setLocationType] = useState("");

  // Settings
  const [containerTypes, setContainerTypes] = useState([]);
  const [locationTypes, setLocationTypes] = useState([]);
  const [allTags, setAllTags] = useState([]);
  const [selectedTagIds, setSelectedTagIds] = useState([]);

  const [loading, setLoading] = useState(false);

  // Receipt scanning state
  const [receiptText, setReceiptText] = useState("");
  const [receiptHtml, setReceiptHtml] = useState("");
  const [parsedProducts, setParsedProducts] = useState([]);
  const [receiptStep, setReceiptStep] = useState("paste");
  const editorRef = useRef(null);

  // Load settings
  useEffect(() => {
    settings.get().then(({ data }) => {
      setContainerTypes(data.containerTypes || []);
      setLocationTypes(data.locationTypes || []);
      if (data.containerTypes?.length) setContainerType(data.containerTypes[0]);
      if (data.locationTypes?.length) setLocationType(data.locationTypes[0]);
    });
    tagsApi.list().then(({ data }) => {
      setAllTags(data);
    });
  }, []);

  // Load products when entering link step or search changes
  const loadProducts = useCallback(async (searchQuery = "") => {
    setProductsLoading(true);
    try {
      const params = {
        entity_type: "product",
        limit: 50,
        sort_by: "created_at",
        sort_order: "desc",
      };
      if (searchQuery.trim()) {
        params.search = searchQuery.trim();
      }
      const { data } = await entities.list(params);
      setProducts(data);
    } catch (err) {
      console.error("Failed to load products:", err);
    }
    setProductsLoading(false);
  }, []);

  // Load products on step change
  useEffect(() => {
    if (step === STEPS.LINK_EXISTING) {
      loadProducts(search);
    }
  }, [step]); // eslint-disable-line react-hooks/exhaustive-deps

  // Debounced search
  useEffect(() => {
    if (step !== STEPS.LINK_EXISTING) return;

    const timer = setTimeout(() => {
      loadProducts(search);
    }, 300);

    return () => clearTimeout(timer);
  }, [search, step, loadProducts]);

  // Search for parent entities
  useEffect(() => {
    if (entityType === "location" || !parentSearch.trim()) {
      setParentSearchResults([]);
      return;
    }

    const timer = setTimeout(() => {
      entities.list({ search: parentSearch, limit: 10 }).then(({ data }) => {
        let filtered = [];
        if (entityType === "product") {
          filtered = data.filter(
            (e) =>
              e.entity_type === "container" || e.entity_type === "location",
          );
        } else if (entityType === "container") {
          filtered = data.filter((e) => e.entity_type === "location");
        }
        setParentSearchResults(filtered);
      });
    }, 300);

    return () => clearTimeout(timer);
  }, [parentSearch, entityType]);

  const getIcon = (type, size = 24) => {
    switch (type) {
      case "product":
        return <ProductIcon size={size} />;
      case "container":
        return <ContainerIcon size={size} />;
      case "location":
        return <LocationIcon size={size} />;
      default:
        return <DocumentIcon size={size} />;
    }
  };

  const handleSelectType = (type) => {
    setEntityType(type);
    if (type === "product") {
      setStep(STEPS.PRODUCT_CHOICE);
    } else {
      // For containers/locations, go straight to create
      setInputMode("manual");
      setStep(STEPS.CREATE_NEW);
    }
  };

  const handleProductChoice = (choice) => {
    if (choice === "link") {
      setStep(STEPS.LINK_EXISTING);
    } else {
      setStep(STEPS.CREATE_NEW);
    }
  };

  const handleInputModeChoice = (mode) => {
    setInputMode(mode);
    if (mode === "receipt") {
      setReceiptStep("paste");
    }
  };

  // Direct link on product click
  const handleLinkToProduct = async (product) => {
    if (linking) return;

    setLinking(true);
    setLinkingId(product.id);
    try {
      await entities.assignQrCode(product.id, qrCode);
      if (onComplete) {
        onComplete(product.id);
      } else {
        navigate(`/entity/${product.id}`);
      }
    } catch (err) {
      console.error("Failed to link QR code:", err);
      alert(err.response?.data?.error || "Failed to link QR code");
      setLinking(false);
      setLinkingId(null);
    }
  };

  const handleSelectParent = (parent) => {
    setSelectedParentId(parent.id);
    setParentEntity(parent);
    setParentSearch(parent.name);
    setShowParentDropdown(false);
  };

  const handleClearParent = () => {
    setSelectedParentId("");
    setParentEntity(null);
    setParentSearch("");
  };

  // Receipt handlers
  const handleReceiptPaste = (e) => {
    e.preventDefault();
    const clipboardData = e.clipboardData;
    if (clipboardData) {
      const html = clipboardData.getData("text/html");
      const text = clipboardData.getData("text/plain");

      if (html) {
        setReceiptHtml(html);
        editorRef.current.innerHTML = html;
      } else {
        editorRef.current.innerText = text;
      }
      setReceiptText(text);
    }
  };

  const handleReceiptInput = () => {
    if (editorRef.current) {
      setReceiptText(editorRef.current.innerText);
      setReceiptHtml(editorRef.current.innerHTML);
    }
  };

  const handleParseReceipt = () => {
    const productLinks = receiptHtml ? extractProductData(receiptHtml) : {};
    const parsedList = parseReceipt(receiptText, productLinks);
    setParsedProducts(parsedList);
    setReceiptStep("select");
  };

  const handleSelectParsedProduct = (product) => {
    setName(product.name);
    if (product.purchasePrice !== null && product.purchasePrice !== undefined) {
      setPurchasePrice(product.purchasePrice.toFixed(2));
    }
    if (product.retailPrice !== null && product.retailPrice !== undefined) {
      setRetailPrice(product.retailPrice.toFixed(2));
      setListSalePrice(product.retailPrice.toFixed(2));
    }
    if (product.quantity) {
      setQuantity(String(product.quantity));
    }
    if (product.asin) {
      setSku(product.asin);
    }
    if (product.url) {
      setProductUrl(product.url);
    }
    if (product.imageUrl) {
      setPendingImageUrl(product.imageUrl);
    }
    if (product.purchaseDate) {
      setPurchaseDate(product.purchaseDate.toISOString().split("T")[0]);
    }
    if (product.source) {
      setDescription(`Added from ${product.source}`);
    }
    setInputMode("manual");
    setReceiptStep("paste");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const data = {
      entity_type: entityType,
      name,
      description: description || undefined,
      parent_id: selectedParentId || undefined,
      qr_code: qrCode,
    };

    if (entityType === "product") {
      if (sku) data.sku = sku;
      if (purchasePrice) data.purchase_price = parseFloat(purchasePrice);
      if (retailPrice) data.retail_price = parseFloat(retailPrice);
      if (minSalePrice) data.min_sale_price = parseFloat(minSalePrice);
      if (listSalePrice) data.list_sale_price = parseFloat(listSalePrice);
      if (quantity) data.quantity = parseInt(quantity, 10);
      if (condition) data.condition = condition;
      if (productUrl) data.url = productUrl;
      if (purchaseDate) data.purchase_date = purchaseDate;
    } else if (entityType === "container") {
      if (capacity) data.capacity = parseInt(capacity, 10);
      if (containerType) data.container_type = containerType;
    } else if (entityType === "location") {
      if (address) data.address = address;
      if (locationType) data.location_type = locationType;
    }

    try {
      const response = await entities.create(data);
      const createdId = response.data.id;

      if (pendingImageUrl && entityType === "product") {
        try {
          await media.downloadFromUrl(pendingImageUrl, createdId);
        } catch (imgErr) {
          console.error("Failed to download image:", imgErr);
        }
      }

      if (selectedTagIds.length > 0) {
        try {
          await entities.setTags(createdId, selectedTagIds);
        } catch (tagErr) {
          console.error("Failed to set tags:", tagErr);
        }
      }

      if (onComplete) {
        onComplete(createdId);
      } else {
        navigate(`/entity/${createdId}`);
      }
    } catch (err) {
      alert(err.response?.data?.error || "Failed to create entity");
      setLoading(false);
    }
  };

  const handleBack = () => {
    if (step === STEPS.PRODUCT_CHOICE) {
      setStep(STEPS.SELECT_TYPE);
      setEntityType(null);
    } else if (step === STEPS.LINK_EXISTING) {
      setStep(STEPS.PRODUCT_CHOICE);
      setSearch("");
      setProducts([]);
    } else if (step === STEPS.CREATE_NEW) {
      if (entityType === "product") {
        if (inputMode === "receipt" && receiptStep === "select") {
          setReceiptStep("paste");
        } else if (inputMode) {
          setInputMode(null);
        } else {
          setStep(STEPS.PRODUCT_CHOICE);
        }
      } else {
        setStep(STEPS.SELECT_TYPE);
        setEntityType(null);
      }
    }
  };

  const getStepTitle = () => {
    switch (step) {
      case STEPS.SELECT_TYPE:
        return "What are you adding?";
      case STEPS.PRODUCT_CHOICE:
        return "Link or Create?";
      case STEPS.LINK_EXISTING:
        return "Select Product";
      case STEPS.CREATE_NEW:
        if (entityType === "product" && !inputMode) {
          return "How do you want to add?";
        }
        if (inputMode === "receipt" && receiptStep === "paste") {
          return "Paste Receipt";
        }
        if (inputMode === "receipt" && receiptStep === "select") {
          return "Select Product";
        }
        return `New ${entityType?.charAt(0).toUpperCase()}${entityType?.slice(1)}`;
      default:
        return "Add to Inventory";
    }
  };

  const getStepSubtitle = () => {
    switch (step) {
      case STEPS.SELECT_TYPE:
        return "Choose the type of item to link to this QR code";
      case STEPS.PRODUCT_CHOICE:
        return "Link to an existing product or create a new one";
      case STEPS.LINK_EXISTING:
        return "Tap a product to link this QR code";
      case STEPS.CREATE_NEW:
        if (entityType === "product" && !inputMode) {
          return "Scan a receipt or enter details manually";
        }
        return "Fill in the details below";
      default:
        return "";
    }
  };

  return (
    <div className="qr-wizard">
      {/* Header */}
      <div className="header">
        <div className="header-content">
          {step !== STEPS.SELECT_TYPE ? (
            <button
              onClick={handleBack}
              className="btn btn-ghost btn-icon"
              disabled={linking}
            >
              <BackIcon size={20} />
            </button>
          ) : (
            <button onClick={onCancel} className="btn btn-ghost btn-icon">
              <CloseIcon size={20} />
            </button>
          )}
          <div className="header-text">
            <h1 className="header-title">{getStepTitle()}</h1>
            <p className="header-subtitle">{getStepSubtitle()}</p>
          </div>
        </div>
      </div>

      <div className="container" style={{ paddingBottom: "80px" }}>
        {/* QR Code Badge */}
        <div
          className="card"
          style={{
            marginBottom: "var(--spacing-lg)",
            padding: "var(--spacing-md)",
            background: "var(--bg-tertiary)",
            display: "flex",
            alignItems: "center",
            gap: "var(--spacing-md)",
          }}
        >
          <span style={{ fontSize: "1.5rem" }}>📱</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: "0.875rem", color: "var(--text-muted)" }}>
              QR Code
            </div>
            <div style={{ fontFamily: "monospace", fontWeight: "600" }}>
              {qrCode}
            </div>
          </div>
        </div>

        {/* Step 1: Select Entity Type */}
        {step === STEPS.SELECT_TYPE && (
          <div className="quick-actions">
            {["product", "container", "location"].map((type) => (
              <div
                key={type}
                onClick={() => handleSelectType(type)}
                className="quick-action"
                style={{ cursor: "pointer" }}
              >
                <div className="quick-action-icon">{getIcon(type, 32)}</div>
                <div
                  className="quick-action-label"
                  style={{ textTransform: "capitalize" }}
                >
                  {type}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Step 2: Product Choice (Link or Create) */}
        {step === STEPS.PRODUCT_CHOICE && (
          <div
            className="quick-actions"
            style={{ gridTemplateColumns: "1fr 1fr" }}
          >
            <div
              onClick={() => handleProductChoice("link")}
              className="quick-action"
              style={{ cursor: "pointer" }}
            >
              <div className="quick-action-icon">
                <LinkIcon size={32} />
              </div>
              <div className="quick-action-label">Link to Existing</div>
              <div
                style={{
                  fontSize: "0.75rem",
                  color: "var(--text-muted)",
                  marginTop: "4px",
                }}
              >
                Connect to a product already in your inventory
              </div>
            </div>
            <div
              onClick={() => handleProductChoice("new")}
              className="quick-action"
              style={{ cursor: "pointer" }}
            >
              <div className="quick-action-icon">
                <ProductIcon size={32} />
              </div>
              <div className="quick-action-label">Create New</div>
              <div
                style={{
                  fontSize: "0.75rem",
                  color: "var(--text-muted)",
                  marginTop: "4px",
                }}
              >
                Add a new product to your inventory
              </div>
            </div>
          </div>
        )}

        {/* Step 3: Link to Existing - Full Product List */}
        {step === STEPS.LINK_EXISTING && (
          <div>
            {/* Search Bar with View Toggle */}
            <div
              style={{
                display: "flex",
                gap: "var(--spacing-sm)",
                marginBottom: "var(--spacing-lg)",
              }}
            >
              <div className="search-bar" style={{ flex: 1, marginBottom: 0 }}>
                <span className="search-icon">
                  <SearchIcon size={18} />
                </span>
                <input
                  type="text"
                  placeholder="Search products..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  autoFocus
                />
              </div>
              <div style={{ display: "flex", gap: "var(--spacing-xs)" }}>
                <button
                  onClick={() => setViewMode("list")}
                  className={`btn btn-icon ${viewMode === "list" ? "btn-primary" : "btn-secondary"}`}
                  title="List view"
                >
                  <ListIcon size={18} />
                </button>
                <button
                  onClick={() => setViewMode("card")}
                  className={`btn btn-icon ${viewMode === "card" ? "btn-primary" : "btn-secondary"}`}
                  title="Card view"
                >
                  <GridIcon size={18} />
                </button>
              </div>
            </div>

            {productsLoading ? (
              <div
                style={{ textAlign: "center", padding: "var(--spacing-xl)" }}
              >
                <div className="spinner" style={{ margin: "0 auto" }}></div>
              </div>
            ) : products.length === 0 ? (
              <div
                className="empty-state"
                style={{ padding: "var(--spacing-xl)" }}
              >
                <div className="empty-state-icon">
                  <ProductIcon size={40} />
                </div>
                <div className="empty-state-title">No products found</div>
                <div className="empty-state-text">
                  {search
                    ? "Try a different search term"
                    : "Add some products first"}
                </div>
              </div>
            ) : viewMode === "list" ? (
              /* Table View */
              <div className="card" style={{ padding: 0, overflow: "hidden" }}>
                <div style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                      <tr
                        style={{
                          borderBottom: "1px solid var(--border-color)",
                          background: "var(--bg-secondary)",
                        }}
                      >
                        <th
                          style={{
                            padding: "var(--spacing-md)",
                            width: "50px",
                          }}
                        ></th>
                        <th
                          style={{
                            padding: "var(--spacing-md)",
                            textAlign: "left",
                            fontWeight: 600,
                            fontSize: "0.875rem",
                          }}
                        >
                          Name
                        </th>
                        <th
                          style={{
                            padding: "var(--spacing-md)",
                            textAlign: "center",
                            fontWeight: 600,
                            fontSize: "0.875rem",
                          }}
                        >
                          Qty
                        </th>
                        <th
                          style={{
                            padding: "var(--spacing-md)",
                            textAlign: "right",
                            fontWeight: 600,
                            fontSize: "0.875rem",
                          }}
                        >
                          Price
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {products.map((product) => (
                        <tr
                          key={product.id}
                          onClick={() => handleLinkToProduct(product)}
                          style={{
                            borderBottom: "1px solid var(--border-color)",
                            cursor: linking ? "wait" : "pointer",
                            opacity:
                              linking && linkingId !== product.id ? 0.5 : 1,
                            background:
                              linkingId === product.id
                                ? "var(--bg-tertiary)"
                                : "transparent",
                          }}
                        >
                          <td
                            style={{
                              padding: "var(--spacing-md)",
                              width: "50px",
                            }}
                          >
                            {product.image_path ? (
                              <img
                                src={media.getImageUrl(product.image_path)}
                                alt=""
                                style={{
                                  width: "40px",
                                  height: "40px",
                                  objectFit: "cover",
                                  borderRadius: "var(--radius-sm)",
                                  background: "var(--bg-secondary)",
                                }}
                              />
                            ) : (
                              <div
                                style={{
                                  width: "40px",
                                  height: "40px",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  background: "var(--bg-secondary)",
                                  borderRadius: "var(--radius-sm)",
                                  color: "var(--text-muted)",
                                }}
                              >
                                <ProductIcon size={20} />
                              </div>
                            )}
                          </td>
                          <td
                            style={{
                              padding: "var(--spacing-md)",
                              textAlign: "left",
                            }}
                          >
                            <div
                              style={{
                                fontWeight: 500,
                                color: "var(--text-primary)",
                              }}
                            >
                              {product.name}
                              {linkingId === product.id && (
                                <span
                                  style={{
                                    marginLeft: "8px",
                                    color: "var(--accent-primary)",
                                  }}
                                >
                                  Linking...
                                </span>
                              )}
                            </div>
                            {product.sku && (
                              <div
                                style={{
                                  fontSize: "0.75rem",
                                  color: "var(--text-muted)",
                                }}
                              >
                                SKU: {product.sku}
                              </div>
                            )}
                          </td>
                          <td
                            style={{
                              padding: "var(--spacing-md)",
                              textAlign: "center",
                            }}
                          >
                            {product.quantity ?? ""}
                          </td>
                          <td
                            style={{
                              padding: "var(--spacing-md)",
                              textAlign: "right",
                            }}
                          >
                            {product.list_sale_price !== null &&
                            product.list_sale_price !== undefined
                              ? `$${parseFloat(product.list_sale_price).toFixed(2)}`
                              : ""}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              /* Card View */
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))",
                  gap: "var(--spacing-md)",
                }}
              >
                {products.map((product) => (
                  <div
                    key={product.id}
                    className="card"
                    onClick={() => handleLinkToProduct(product)}
                    style={{
                      aspectRatio: "1",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: linking ? "wait" : "pointer",
                      textAlign: "center",
                      padding: "var(--spacing-md)",
                      opacity: linking && linkingId !== product.id ? 0.5 : 1,
                      borderColor:
                        linkingId === product.id
                          ? "var(--accent-primary)"
                          : undefined,
                    }}
                  >
                    {product.image_path ? (
                      <img
                        src={media.getImageUrl(product.image_path)}
                        alt=""
                        style={{
                          width: "80px",
                          height: "80px",
                          objectFit: "contain",
                          marginBottom: "var(--spacing-sm)",
                          borderRadius: "var(--radius-sm)",
                        }}
                      />
                    ) : (
                      <div
                        style={{
                          marginBottom: "var(--spacing-sm)",
                          color: "var(--text-secondary)",
                        }}
                      >
                        <ProductIcon size={40} />
                      </div>
                    )}
                    <div
                      style={{
                        fontWeight: 600,
                        fontSize: "0.875rem",
                        marginBottom: "var(--spacing-xs)",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        width: "100%",
                      }}
                    >
                      {product.name}
                    </div>
                    {linkingId === product.id ? (
                      <div
                        style={{
                          color: "var(--accent-primary)",
                          fontSize: "0.75rem",
                        }}
                      >
                        Linking...
                      </div>
                    ) : product.list_sale_price !== null &&
                      product.list_sale_price !== undefined ? (
                      <div
                        style={{
                          color: "var(--accent-primary)",
                          fontWeight: 600,
                        }}
                      >
                        ${parseFloat(product.list_sale_price).toFixed(2)}
                      </div>
                    ) : null}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Step 4: Create New */}
        {step === STEPS.CREATE_NEW && (
          <div>
            {/* Input mode selection for products */}
            {entityType === "product" && !inputMode && (
              <div
                className="quick-actions"
                style={{ gridTemplateColumns: "1fr 1fr" }}
              >
                <div
                  onClick={() => handleInputModeChoice("receipt")}
                  className="quick-action"
                  style={{ cursor: "pointer" }}
                >
                  <div className="quick-action-icon">
                    <DocumentIcon size={32} />
                  </div>
                  <div className="quick-action-label">Scan Receipt</div>
                  <div
                    style={{
                      fontSize: "0.75rem",
                      color: "var(--text-muted)",
                      marginTop: "4px",
                    }}
                  >
                    Paste from Amazon or other receipts
                  </div>
                </div>
                <div
                  onClick={() => handleInputModeChoice("manual")}
                  className="quick-action"
                  style={{ cursor: "pointer" }}
                >
                  <div className="quick-action-icon">
                    <ProductIcon size={32} />
                  </div>
                  <div className="quick-action-label">Manual Entry</div>
                  <div
                    style={{
                      fontSize: "0.75rem",
                      color: "var(--text-muted)",
                      marginTop: "4px",
                    }}
                  >
                    Enter product details yourself
                  </div>
                </div>
              </div>
            )}

            {/* Receipt scanning for products */}
            {entityType === "product" && inputMode === "receipt" && (
              <div>
                {receiptStep === "paste" && (
                  <>
                    <p
                      style={{
                        fontSize: "var(--text-small)",
                        color: "var(--text-secondary)",
                        marginBottom: "var(--spacing-md)",
                      }}
                    >
                      Paste your receipt to auto-fill product details. Works
                      with Amazon orders.
                    </p>
                    <div
                      ref={editorRef}
                      className="quick-add-textarea quick-add-editor"
                      contentEditable
                      onPaste={handleReceiptPaste}
                      onInput={handleReceiptInput}
                      data-placeholder="Paste receipt text here..."
                      style={{
                        minHeight: "200px",
                        padding: "var(--spacing-md)",
                        background: "var(--bg-primary)",
                        border: "1px solid var(--border-color)",
                        borderRadius: "var(--radius-md)",
                        marginBottom: "var(--spacing-md)",
                      }}
                    />
                    <div style={{ display: "flex", gap: "var(--spacing-md)" }}>
                      <button
                        className="btn btn-secondary"
                        style={{ flex: 1 }}
                        onClick={() => handleInputModeChoice("manual")}
                      >
                        Skip to Manual
                      </button>
                      <button
                        className="btn btn-primary"
                        style={{ flex: 1 }}
                        onClick={handleParseReceipt}
                        disabled={!receiptText.trim()}
                      >
                        Parse Receipt
                      </button>
                    </div>
                  </>
                )}

                {receiptStep === "select" && (
                  <>
                    {parsedProducts.length === 0 ? (
                      <div
                        className="empty-state"
                        style={{ padding: "var(--spacing-xl)" }}
                      >
                        <div className="empty-state-icon">
                          <ProductIcon size={40} />
                        </div>
                        <div className="empty-state-title">
                          No products found
                        </div>
                        <div className="empty-state-text">
                          We couldn't detect any products in the pasted text.
                        </div>
                        <button
                          className="btn btn-secondary"
                          onClick={() => setReceiptStep("paste")}
                        >
                          Try Again
                        </button>
                      </div>
                    ) : (
                      <>
                        <p
                          style={{
                            fontSize: "var(--text-small)",
                            color: "var(--text-secondary)",
                            marginBottom: "var(--spacing-md)",
                          }}
                        >
                          Found {parsedProducts.length} product
                          {parsedProducts.length !== 1 ? "s" : ""}. Select one
                          to fill the form:
                        </p>
                        <div className="item-list">
                          {parsedProducts.map((product, index) => (
                            <div
                              key={index}
                              className="item-row"
                              onClick={() => handleSelectParsedProduct(product)}
                              style={{ cursor: "pointer" }}
                            >
                              {product.imageUrl && (
                                <img
                                  src={product.imageUrl}
                                  alt=""
                                  style={{
                                    width: "40px",
                                    height: "40px",
                                    objectFit: "cover",
                                    borderRadius: "var(--radius-md)",
                                  }}
                                  onError={(e) => {
                                    e.target.style.display = "none";
                                  }}
                                />
                              )}
                              <div className="item-row-content">
                                <div className="item-row-title">
                                  {product.name}
                                  {product.url && (
                                    <span
                                      style={{
                                        marginLeft: "6px",
                                        color: "var(--accent-primary)",
                                      }}
                                    >
                                      <LinkIcon size={12} />
                                    </span>
                                  )}
                                </div>
                                <div className="item-row-meta">
                                  {product.purchasePrice !== null &&
                                    product.purchasePrice !== undefined && (
                                      <span>
                                        ${product.purchasePrice.toFixed(2)}
                                      </span>
                                    )}
                                  {product.quantity > 1 && (
                                    <span style={{ marginLeft: "8px" }}>
                                      Qty: {product.quantity}
                                    </span>
                                  )}
                                </div>
                              </div>
                              <ChevronRightIcon size={16} />
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                  </>
                )}
              </div>
            )}

            {/* Manual form */}
            {(inputMode === "manual" || entityType !== "product") && (
              <form onSubmit={handleSubmit}>
                <div className="card">
                  <div className="form-group">
                    <label>Name *</label>
                    <input
                      type="text"
                      placeholder={`Enter ${entityType} name`}
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      required
                      autoFocus
                    />
                  </div>

                  <div className="form-group">
                    <label>Description</label>
                    <textarea
                      placeholder="Optional description"
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      rows="3"
                    />
                  </div>

                  {/* Parent selector */}
                  {entityType !== "location" && (
                    <div
                      className="form-group"
                      style={{ position: "relative" }}
                    >
                      <label>
                        {entityType === "product"
                          ? "Location / Container"
                          : "Location"}
                      </label>
                      {selectedParentId && parentEntity ? (
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "var(--spacing-sm)",
                            padding: "var(--spacing-sm) var(--spacing-md)",
                            background: "var(--bg-tertiary)",
                            borderRadius: "var(--radius-md)",
                            border: "1px solid var(--border-color)",
                          }}
                        >
                          <span>{getIcon(parentEntity.entity_type, 16)}</span>
                          <span style={{ flex: 1 }}>{parentEntity.name}</span>
                          <button
                            type="button"
                            onClick={handleClearParent}
                            className="btn btn-ghost btn-icon"
                            style={{ padding: "4px 8px" }}
                          >
                            <CloseIcon size={16} />
                          </button>
                        </div>
                      ) : (
                        <>
                          <input
                            type="text"
                            placeholder="Search for location or container..."
                            value={parentSearch}
                            onChange={(e) => {
                              setParentSearch(e.target.value);
                              setShowParentDropdown(true);
                            }}
                            onFocus={() => setShowParentDropdown(true)}
                            onBlur={() =>
                              setTimeout(
                                () => setShowParentDropdown(false),
                                200,
                              )
                            }
                          />
                          {showParentDropdown &&
                            parentSearchResults.length > 0 && (
                              <div
                                style={{
                                  position: "absolute",
                                  top: "100%",
                                  left: 0,
                                  right: 0,
                                  background: "var(--bg-card)",
                                  border: "1px solid var(--border-color)",
                                  borderRadius: "var(--radius-md)",
                                  boxShadow: "var(--shadow-lg)",
                                  zIndex: 100,
                                  maxHeight: "200px",
                                  overflowY: "auto",
                                }}
                              >
                                {parentSearchResults.map((p) => (
                                  <div
                                    key={p.id}
                                    onClick={() => handleSelectParent(p)}
                                    style={{
                                      padding:
                                        "var(--spacing-sm) var(--spacing-md)",
                                      cursor: "pointer",
                                      display: "flex",
                                      alignItems: "center",
                                      gap: "var(--spacing-sm)",
                                      borderBottom:
                                        "1px solid var(--border-color)",
                                    }}
                                    onMouseEnter={(e) =>
                                      (e.target.style.background =
                                        "var(--bg-tertiary)")
                                    }
                                    onMouseLeave={(e) =>
                                      (e.target.style.background =
                                        "transparent")
                                    }
                                  >
                                    <span>{getIcon(p.entity_type, 16)}</span>
                                    <span>{p.name}</span>
                                  </div>
                                ))}
                              </div>
                            )}
                        </>
                      )}
                    </div>
                  )}

                  {/* Product fields */}
                  {entityType === "product" && (
                    <>
                      <div className="form-group">
                        <label>SKU / Barcode</label>
                        <input
                          type="text"
                          placeholder="Product identifier (optional)"
                          value={sku}
                          onChange={(e) => setSku(e.target.value)}
                        />
                      </div>

                      <div className="grid grid-2">
                        <div className="form-group">
                          <label>Quantity</label>
                          <input
                            type="number"
                            placeholder="1"
                            value={quantity}
                            onChange={(e) => setQuantity(e.target.value)}
                            min="0"
                          />
                        </div>
                        <div className="form-group">
                          <label>Condition</label>
                          <SearchableSelect
                            options={[
                              { value: "new", label: "New" },
                              { value: "like_new", label: "Like New" },
                              { value: "good", label: "Good" },
                              { value: "fair", label: "Fair" },
                              { value: "poor", label: "Poor" },
                            ]}
                            value={condition}
                            onChange={(val) => setCondition(val || "new")}
                          />
                        </div>
                      </div>

                      <div className="grid grid-2">
                        <div className="form-group">
                          <label>Purchase Price</label>
                          <input
                            type="number"
                            placeholder="0.00"
                            value={purchasePrice}
                            onChange={(e) => setPurchasePrice(e.target.value)}
                            step="0.01"
                          />
                        </div>
                        <div className="form-group">
                          <label>Retail Price</label>
                          <input
                            type="number"
                            placeholder="0.00"
                            value={retailPrice}
                            onChange={(e) => setRetailPrice(e.target.value)}
                            step="0.01"
                          />
                        </div>
                      </div>

                      <div className="grid grid-2" style={{ marginBottom: 0 }}>
                        <div className="form-group">
                          <label>Minimum Sale Price</label>
                          <input
                            type="number"
                            placeholder="0.00"
                            value={minSalePrice}
                            onChange={(e) => setMinSalePrice(e.target.value)}
                            step="0.01"
                          />
                        </div>
                        <div className="form-group">
                          <label>List Sale Price</label>
                          <input
                            type="number"
                            placeholder="0.00"
                            value={listSalePrice}
                            onChange={(e) => setListSalePrice(e.target.value)}
                            step="0.01"
                          />
                        </div>
                      </div>
                    </>
                  )}

                  {/* Container fields */}
                  {entityType === "container" && (
                    <>
                      <div className="grid grid-2" style={{ marginBottom: 0 }}>
                        <div className="form-group">
                          <label>Container Type</label>
                          <SearchableSelect
                            options={containerTypes.map((type) => ({
                              value: type,
                              label:
                                type.charAt(0).toUpperCase() + type.slice(1),
                            }))}
                            value={containerType}
                            onChange={(val) => setContainerType(val)}
                          />
                        </div>
                        <div className="form-group">
                          <label>Capacity</label>
                          <input
                            type="number"
                            placeholder="Max items (optional)"
                            value={capacity}
                            onChange={(e) => setCapacity(e.target.value)}
                            min="0"
                          />
                        </div>
                      </div>
                    </>
                  )}

                  {/* Location fields */}
                  {entityType === "location" && (
                    <>
                      <div className="form-group">
                        <label>Location Type</label>
                        <SearchableSelect
                          options={locationTypes.map((type) => ({
                            value: type,
                            label: type.charAt(0).toUpperCase() + type.slice(1),
                          }))}
                          value={locationType}
                          onChange={(val) => setLocationType(val)}
                        />
                      </div>
                      <div className="form-group" style={{ marginBottom: 0 }}>
                        <label>Address</label>
                        <textarea
                          placeholder="Physical address (optional)"
                          value={address}
                          onChange={(e) => setAddress(e.target.value)}
                          rows="2"
                        />
                      </div>
                    </>
                  )}

                  {/* Tags */}
                  <div style={{ marginTop: "var(--spacing-md)" }}>
                    <label>Tags</label>
                    <TagMultiSelect
                      allTags={allTags}
                      selectedTagIds={selectedTagIds}
                      onTagsChange={setSelectedTagIds}
                      onCreateTag={async (tagName) => {
                        const { data } = await tagsApi.create({
                          name: tagName,
                        });
                        setAllTags([...allTags, data]);
                        return data;
                      }}
                    />
                  </div>

                  <button
                    type="submit"
                    className="btn btn-primary btn-lg"
                    disabled={loading}
                    style={{ width: "100%", marginTop: "var(--spacing-lg)" }}
                  >
                    {loading ? "Creating..." : `Create ${entityType}`}
                  </button>
                </div>
              </form>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default QRWizard;
